import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Hexagon, Copy, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIVE_COUNTS = ["I don't have hives yet", "1–3", "4–10", "11–25", "26–50", "50+"];
const RECORD_METHODS = ["Paper notebook", "Spreadsheet", "Another app", "From memory", "Nothing yet"];
const HEARD_FROM = ["Friend or fellow beekeeper", "Social media", "Beekeeping forum or group", "Podcast", "Web search", "Other"];
const INTERESTS = [
  "Hive health tracking",
  "Inspection logging",
  "AI-powered insights",
  "BeeCredits™ rewards",
  "Ask a Bee™ Q&A",
  "Honey production records",
];

const fieldCls =
  "bg-white border-forest/15 focus-visible:ring-honey focus-visible:ring-2 focus-visible:ring-offset-0 rounded-xl h-12";

const initialForm = {
  first_name: "",
  email: "",
  hive_count: "",
  record_method: "",
  interests: [],
  biggest_challenge: "",
  beta_interest: false,
  heard_from: "",
  referred: "no",
  referral_name: "",
  referral_email: "",
  referral_code: "",
  marketing_consent: true,
  tips_series: false,
};

export default function PreRegister() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialForm);
  const [source, setSource] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referred: "yes", referral_code: ref.toUpperCase() }));
    }
    const src = searchParams.get("src");
    if (src) setSource(src);
  }, [searchParams]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleInterest = (interest) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter((i) => i !== interest)
        : [...f.interests, interest],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please enter your first name and email.");
      return;
    }
    if (!form.hive_count || !form.record_method) {
      toast.error("Please tell us your hive count and record-keeping method.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        email: form.email.trim(),
        hive_count: form.hive_count,
        record_method: form.record_method,
        interests: form.interests,
        biggest_challenge: form.biggest_challenge.trim() || null,
        beta_interest: form.beta_interest,
        heard_from: form.heard_from || null,
        referred: form.referred === "yes",
        referral_name: form.referred === "yes" ? form.referral_name.trim() || null : null,
        referral_email: form.referred === "yes" ? form.referral_email.trim() || null : null,
        referral_code: form.referred === "yes" ? form.referral_code.trim() || null : null,
        marketing_consent: form.marketing_consent,
        tips_series: form.tips_series,
        source,
      };
      const { data } = await axios.post(`${API}/pre-register`, payload);
      setSuccess({ name: form.first_name.trim(), code: data.referral_code, link: data.referral_link, tips: form.tips_series });
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.info(typeof detail === "string" ? detail : "This email is already on the launch list.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(success.link);
      toast.success("Referral link copied!");
    } catch {
      toast.error("Couldn't copy automatically — please copy the link manually.");
    }
  };

  if (success) {
    return (
      <section className="max-w-2xl mx-auto px-6 py-24 md:py-36 text-center" data-testid="pre-register-success">
        <FadeUp>
          <CheckCircle2 className="w-16 h-16 text-honey-dark mx-auto mb-8" strokeWidth={1.5} />
          <h1 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-forest mb-6" data-testid="success-headline">
            You're on the list! 🐝
          </h1>
          <p className="text-ink/60 leading-relaxed max-w-md mx-auto mb-4">
            Welcome to the ApiaryKeeper launch list, {success.name}. A confirmation
            is on its way to your inbox — early access invites go to this list first.
          </p>
          {success.tips && (
            <p className="text-honey-dark text-sm font-medium mb-8" data-testid="success-tips-note">
              Beekeeping Basics Tip 1 of 4 is already on its way to your inbox.
            </p>
          )}
          {!success.tips && <div className="mb-8" />}
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="bg-white border border-honey/60 rounded-3xl p-8 md:p-10" data-testid="referral-code-card">
            <p className="text-xs uppercase tracking-[0.25em] text-honey-dark mb-3">Your personal referral code</p>
            <p className="font-display text-5xl font-semibold tracking-[0.15em] text-forest mb-6" data-testid="referral-code-value">
              {success.code}
            </p>
            <div className="flex items-center gap-3 bg-bone border border-forest/15 rounded-xl px-4 py-3 mb-6">
              <Link2 className="w-4 h-4 text-honey-dark shrink-0" />
              <span className="text-sm text-ink/60 truncate" data-testid="referral-link-value">{success.link}</span>
            </div>
            <button
              type="button"
              onClick={copyLink}
              data-testid="copy-referral-link-button"
              className="inline-flex items-center gap-2 bg-forest text-bone font-semibold px-8 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-honey hover:text-forest"
            >
              <Copy className="w-4 h-4" />
              Copy My Referral Link
            </button>
            <p className="text-xs text-ink/40 mt-5">
              Earn BeeCredits™ for every beekeeper who joins with your link and completes the qualifying steps.
            </p>
          </div>
        </FadeUp>
      </section>
    );
  }

  return (
    <>
      <section className="bg-forest-deep text-bone" data-testid="pre-register-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-honey mb-6">Pre-Register</p>
          </FadeUp>
          <h1 className="font-display font-medium tracking-tighter leading-[0.95] text-[clamp(2.4rem,5.5vw,4.5rem)]" data-testid="pre-register-heading">
            <MaskedLines
              lines={[
                <span key="a">Join the ApiaryKeeper</span>,
                <span key="b"><em className="text-honey not-italic font-semibold">Launch List.</em></span>,
              ]}
              delay={0.2}
            />
          </h1>
          <FadeUp delay={0.7} className="mt-6 max-w-xl">
            <p className="text-bone/60 leading-relaxed">
              Two minutes now, first-in-line access at launch. Tell us a little about
              your bees so we build the right thing.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-14 md:py-20" data-testid="pre-register-form-section">
        <FadeUp>
          <form onSubmit={handleSubmit} className="flex flex-col gap-10" data-testid="pre-register-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <Label htmlFor="first_name" className="text-forest font-medium">First Name *</Label>
                <Input
                  id="first_name"
                  data-testid="input-first-name"
                  className={fieldCls}
                  value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)}
                  placeholder="Wren"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email" className="text-forest font-medium">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  className={fieldCls}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <Label className="text-forest font-medium">How many hives do you keep? *</Label>
                <Select value={form.hive_count} onValueChange={(v) => set("hive_count", v)}>
                  <SelectTrigger data-testid="select-hive-count" className={fieldCls}>
                    <SelectValue placeholder="Select hive count" />
                  </SelectTrigger>
                  <SelectContent>
                    {HIVE_COUNTS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`hive-count-option-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-forest font-medium">How do you keep records today? *</Label>
                <Select value={form.record_method} onValueChange={(v) => set("record_method", v)}>
                  <SelectTrigger data-testid="select-record-method" className={fieldCls}>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECORD_METHODS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`record-method-option-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Label className="text-forest font-medium">What do you want to use ApiaryKeeper for? <span className="text-ink/40 font-normal">(pick any)</span></Label>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="interests-group">
                {INTERESTS.map((interest) => (
                  <label
                    key={interest}
                    className={`flex items-center gap-3 border rounded-xl px-4 py-3.5 cursor-pointer transition-colors duration-300 ${
                      form.interests.includes(interest)
                        ? "border-honey bg-honey/10"
                        : "border-forest/15 bg-white hover:border-honey/60"
                    }`}
                  >
                    <Checkbox
                      data-testid={`interest-${interest.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      checked={form.interests.includes(interest)}
                      onCheckedChange={() => toggleInterest(interest)}
                      className="border-forest/30 data-[state=checked]:bg-honey data-[state=checked]:border-honey data-[state=checked]:text-forest"
                    />
                    <span className="text-sm text-ink/80">{interest}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="biggest_challenge" className="text-forest font-medium">
                What's your biggest apiary challenge? <span className="text-ink/40 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="biggest_challenge"
                data-testid="input-biggest-challenge"
                className="bg-white border-forest/15 focus-visible:ring-honey focus-visible:ring-2 focus-visible:ring-offset-0 rounded-xl min-h-28"
                value={form.biggest_challenge}
                onChange={(e) => set("biggest_challenge", e.target.value)}
                placeholder="Varroa, overwintering losses, remembering treatments..."
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <Label className="text-forest font-medium">How did you hear about us?</Label>
                <Select value={form.heard_from} onValueChange={(v) => set("heard_from", v)}>
                  <SelectTrigger data-testid="select-heard-from" className={fieldCls}>
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent>
                    {HEARD_FROM.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`heard-from-option-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-forest font-medium">Interested in beta testing?</Label>
                <RadioGroup
                  value={form.beta_interest ? "yes" : "no"}
                  onValueChange={(v) => set("beta_interest", v === "yes")}
                  className="flex gap-6 h-12 items-center"
                  data-testid="beta-interest-toggle"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="yes" data-testid="beta-interest-yes" className="border-forest/30 text-honey-dark" />
                    <span className="text-sm text-ink/80">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="no" data-testid="beta-interest-no" className="border-forest/30 text-honey-dark" />
                    <span className="text-sm text-ink/80">No</span>
                  </label>
                </RadioGroup>
              </div>
            </div>

            <div className="border border-forest/15 rounded-2xl p-6 md:p-8 bg-white" data-testid="referral-section">
              <Label className="text-forest font-medium">Were you referred by someone?</Label>
              <RadioGroup
                value={form.referred}
                onValueChange={(v) => set("referred", v)}
                className="flex gap-6 mt-3"
                data-testid="referral-toggle"
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="no" data-testid="referral-no" className="border-forest/30 text-honey-dark" />
                  <span className="text-sm text-ink/80">No</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="yes" data-testid="referral-yes" className="border-forest/30 text-honey-dark" />
                  <span className="text-sm text-ink/80">Yes</span>
                </label>
              </RadioGroup>

              {form.referred === "yes" && (
                <div className="grid md:grid-cols-3 gap-4 mt-6" data-testid="referral-fields">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="referral_name" className="text-sm text-ink/70">Their name</Label>
                    <Input
                      id="referral_name"
                      data-testid="input-referral-name"
                      className={fieldCls}
                      value={form.referral_name}
                      onChange={(e) => set("referral_name", e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="referral_email" className="text-sm text-ink/70">Their email</Label>
                    <Input
                      id="referral_email"
                      type="email"
                      data-testid="input-referral-email"
                      className={fieldCls}
                      value={form.referral_email}
                      onChange={(e) => set("referral_email", e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="referral_code" className="text-sm text-ink/70">Referral code</Label>
                    <Input
                      id="referral_code"
                      data-testid="input-referral-code"
                      className={fieldCls}
                      value={form.referral_code}
                      onChange={(e) => set("referral_code", e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <label
              className={`flex items-center gap-3 border rounded-xl px-4 py-4 cursor-pointer transition-colors duration-300 ${
                form.tips_series ? "border-honey bg-honey/10" : "border-forest/15 bg-white hover:border-honey/60"
              }`}
              data-testid="tips-series-label"
            >
              <Checkbox
                data-testid="checkbox-tips-series"
                checked={form.tips_series}
                onCheckedChange={(v) => set("tips_series", !!v)}
                className="border-forest/30 data-[state=checked]:bg-honey data-[state=checked]:border-honey data-[state=checked]:text-forest"
              />
              <span className="text-sm text-ink/80 leading-relaxed">
                Send me the free <span className="font-medium text-forest">Beekeeping Basics</span> email
                series — one practical tip every few days until launch. 🐝
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="marketing-consent-label">
              <Checkbox
                data-testid="checkbox-marketing-consent"
                checked={form.marketing_consent}
                onCheckedChange={(v) => set("marketing_consent", !!v)}
                className="mt-0.5 border-forest/30 data-[state=checked]:bg-honey data-[state=checked]:border-honey data-[state=checked]:text-forest"
              />
              <span className="text-sm text-ink/60 leading-relaxed">
                I agree to receive launch updates and occasional emails from ApiaryKeeper
                (Herrell Interactive). Unsubscribe anytime.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="pre-register-submit-button"
              className="inline-flex items-center justify-center gap-3 bg-forest text-bone font-semibold px-10 py-5 rounded-full text-base transition-colors duration-300 hover:bg-honey hover:text-forest disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Joining the list...
                </>
              ) : (
                <>
                  <Hexagon className="w-5 h-5" strokeWidth={1.5} />
                  Join the ApiaryKeeper Launch List
                </>
              )}
            </button>
          </form>
        </FadeUp>
      </section>
    </>
  );
}
